import { UiCommand, UiCommandEvent } from '@ui/cer-ui-types';
import { CerFormAdapter } from '@form/cer-form-adapter';

import { CerFormViewSet } from '@form/cer-form-view-set';
import { CerFormView, ICerViewComponent } from '@form/cer-form-view';
import { CerFormPanesSettingsUtil, uiCmdPaneRightToggle } from '@form/cer-form-panes/cer-form-panes.service';
import { FieldWidthEnum } from 'src/cer/cer-data/cer-data-types';
import { ExchangeMsgClient } from 'src/app/api';
import { CerUiIcons } from '@ui/cer-ui-icons';

const uiCmdExchangeRerun: UiCommand = { id: 'ExchangeRerun', text: 'Kør markeret igen', iconCss: CerUiIcons.research, tooltipText: 'Kør integration igen for markeret post', target: ".e-content", needSelection: true };

export class ExchangeMsgForm extends CerFormAdapter {

  public override viewSetInit(viewSet: CerFormViewSet): void {
    this.setInitialFilter(this.routeOptions.routeData);

    viewSet.panes = CerFormPanesSettingsUtil.masterDetail('horizontal', false, 30);
    const view = this.exchangeMsgViewGet();
    const logView = this.logViewGet();
    viewSet.viewAdd(view);
    viewSet.viewAdd(logView, view);
  }

  // Parameters
  private filterWaiting: boolean;
  private filterFailed: boolean;

  private setInitialFilter(routeData: any) {
    if (routeData != null) {
      this.filterWaiting = (routeData['waiting'] == true);
      this.filterFailed = (routeData['failed'] == true);
    }
  }

  // View
  private exchangeMsgViewGet(): CerFormView {
    const view = new CerFormView();
    
    view.onCommandEvent = (e => this.onCommand(e));

    view.viewMetadata = {
      name: 'data-udveksling-status', text: 'Udvekslinger',
      primaryKey: 'id',
      allowEdit: false, allowCreate: false, allowDelete: true,
      dataApiName: 'ExchangeMsgView',
    };

    view.fieldMetadata = [
      { name: 'id', text: 'Id', tooltipText: 'Unik nøgle for posten', allowEdit: false, visible: false },
      { name: 'owner', text: 'Afsender', tooltipText: 'Afsender af besked', widthType: FieldWidthEnum.M, foreignKeyTableName: 'ExchangeParty' },
      { name: 'ownerId', text: 'Post Id', tooltipText: 'Id for posten der afsendes', visible: false },
      { name: 'created', text: 'Oprettet', tooltipText: 'Dato for oprettelse', format: 'DateLong', orderBy: 'Descending' },
      { name: 'createdBy', text: 'Oprettet af', tooltipText: 'Bruger der har oprettet', foreignKeyTableName: 'User' },
      { name: 'type', text: 'Integration', tooltipText: 'Beskrivelse beskedtype', widthType: FieldWidthEnum.L, foreignKeyTableName: 'ExchangeType' },
      { name: 'num', text: 'Nr', tooltipText: 'Nøgle for post', widthType: FieldWidthEnum.M },
      { name: 'msgClientId', text: 'Oprettet post', tooltipText: 'Post id hos modtager', visible: false, format: 'TextRight' },
      { name: 'subscriberId', text: 'Modtager', tooltipText: 'Modtageraf af dataudvekling', widthType: FieldWidthEnum.M, foreignKeyTableName: 'ExchangeParty' },
      { name: 'msgSubscriberId', text: 'Modtager besked Id', tooltipText: 'Id for posten der modtages', visible: false },
      { name: 'subscriberPickedUp', text: 'Modtaget', tooltipText: 'Modtaget dato', format: 'DateLong' },
      { name: 'subscriberProcessed', text: 'Behandlet', tooltipText: 'Behandlet dato hos modtager', format: 'DateLong', visible: false },
      { name: 'isFailed', text: 'Fejl', tooltipText: 'Er fejlet hos modtager?', format: 'CheckBox' },
      { name: 'isPickedUp', text: 'Modtaget', tooltipText: 'Er modtaget hos modtager?', format: 'CheckBox' },
      { name: 'isProcessed', text: 'Behandlet', tooltipText: 'Er behandlet hos modtager?', format: 'CheckBox' }
    ];

    const rightToggle : UiCommand= { ... uiCmdPaneRightToggle, ...{ text: 'Vis log' } };
    view.toolbarCommands = [uiCmdExchangeRerun, rightToggle];
    view.contextMenuCommands = [uiCmdExchangeRerun, rightToggle];

    if (this.filterWaiting) {
      view.toolbarCommands = view.toolbarCommands.filter(i => i.id != uiCmdExchangeRerun.id);
      view.contextMenuCommands = view.contextMenuCommands.filter(i => i.id != uiCmdExchangeRerun.id);

      view.fieldMetadata.filter(f => (f.name == 'isPickedUp')).forEach(f => {
        f.filterOperator = "equal";
        f.filterValue = false;
      });
    }

    if (this.filterFailed) {
      view.fieldMetadata.filter(f => (f.name == 'isFailed')).forEach(f => {
        f.filterOperator = "equal";
        f.filterValue = true;
      });
    }

    return view;
  }

  // Log view
  private logViewGet(): CerFormView {
    const view = new CerFormView();

    view.mandatory = true;
    view.visibleDefault = 'collapse';
    view.viewMetadata = {
      name: 'data-udveksling-log', text: 'Log',
      primaryKey: 'id', parentPrimaryKey: 'msgSubscriberId', parentForeignKey: 'msgSubscriberId',
      allowEdit: false, allowCreate: false, allowDelete: false,
      dataApiName: 'ExchangeMsgSubscriberLog'
    };

    view.fieldMetadata = [
      { name: 'id', text: 'Id', tooltipText: 'Unik nøgle for posten', allowEdit: false, visible: false },
      { name: 'log', text: 'Log', tooltipText: 'Besked fra ny status', allowEdit: false, widthType: FieldWidthEnum.XXL },
      { name: 'created', text: 'Oprettet', tooltipText: 'Dato for oprettelse', allowEdit: false, format: 'DateLong', widthType: FieldWidthEnum.S },
      { name: 'msgSubscriberId', text: 'Modtager besked Id', tooltipText: 'Modtager besked id', allowEdit: false, allowEditOnAdd: true, visible: false }
    ];

    return view;
  }

  // Commands
  public onCommand(event: UiCommandEvent) {
    switch (event.commandId) {
      case uiCmdExchangeRerun.id:
        this.reRunExchange(event.rowData?.msgSubscriberId, event.appComponent);
        break;
    }
  }

  private reRunExchange(msgSubscriberId: number, c: ICerViewComponent) {
    if (msgSubscriberId) {
      const client : ExchangeMsgClient = this.context.injector.get(ExchangeMsgClient);
      client.rerunSubscriber(msgSubscriberId).subscribe(ok => c.rowSelectedRefresh());
    }
  }

  private exchangeLogShowHide(c: ICerViewComponent) {
    this.context.form.panesService.toggle('right');
    c.rowSelectedRefresh();
  }
}
